import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SessionService } from 'account-hybrid/core/authentication';
import { DateRange } from 'apps/account/src/account-shared';
import { dateSerialize } from 'apps/account/src/account-shared/pipes/date-serializer.util';
import { SnapshotService } from 'apps/account/src/account-shared/services/snapshot.service';
// account-v2 (AngularJS) NgRx action thunks — plain ES modules, NOT AngularJS
// services, so they import through the TS module system rather than $injector.
// The thunks are still dispatched through the SAME ng-redux store via $ngRedux,
// so reducers + thunk-extras (SpacesService) work unchanged.
import * as BigPictureActions from '../../../../account-v2/src/app/features/advocacy/group/big-picture/actions';
import AdvocacyImpactApi from '../../../../account-v2/src/app/actions/advocacyImpactApi';
import { AdvocacyNgReduxFacade } from '../store/advocacy-ngredux.facade';

/**
 * Native-Angular port of the AngularJS `ftBigPicture` controller
 * (account-v2/.../advocacy/group/big-picture/bigPicture.component.ts).
 *
 * Drives the /advocacy/big-picture leaf. Keeps the AngularJS-side NgRx
 * (ng-redux) store as the source of truth — actions/reducers/selectors
 * unchanged. Bridges to it via `$injector.get('$ngRedux')` for read +
 * dispatch. This is the reference pattern for the remaining leaf ports;
 * keep it tight + commented.
 */
const LAYERS_ORDER = ['Homepage', 'Pre-Stay', 'Post-Stay', 'Photo Contest'] as const;

interface LayerInfoRequestStatus {
  name: string;
  isLoaded: boolean;
}

interface SectionEntry {
  name: string;
  title: string;
  layerName: typeof LAYERS_ORDER[number] | 'Quote List';
  isActive: boolean;
  isDisabled: boolean;
}

@Component({
    selector: 'ft-big-picture',
    templateUrl: './big-picture.component.html',
    standalone: false
})
export class BigPictureComponent implements OnInit, OnDestroy {
  property: any;
  propertyAdvocacyOverview: any;
  propertyAdvocacyImpact: any = {};
  layerInfoRequestStatus: LayerInfoRequestStatus[] = [];
  isInitialized = false;
  startDate?: Date;
  endDate?: Date;
  sectionsRegistry: SectionEntry[] = [
    { name: 'website', title: 'Your website', layerName: 'Homepage', isActive: false, isDisabled: false },
    { name: 'bookingengine', title: 'Booking Engine', layerName: 'Pre-Stay', isActive: false, isDisabled: false },
    { name: 'poststay', title: 'Story Builder', layerName: 'Post-Stay', isActive: false, isDisabled: false },
    { name: 'photoAdvocacy', title: 'Storytellers', layerName: 'Photo Contest', isActive: false, isDisabled: false },
  ];

  private unsubscribe?: () => void;
  private $ngRedux: any;
  // WS5 #10 inc 2r: the native SnapshotService (field initializer — inject() in
  // ngOnInit throws NG0203). Same sessionStorage key + Past-30-Days default as
  // the v1 'snapshot' factory this component grabbed via $injector.
  private snapshot = inject(SnapshotService);
  private dateSerializer: any;
  private layerApiMap: Record<string, (uuid: string, start: string, end: string) => any> = {};

  constructor(
    private readonly spacesService: SpacesService,
    private readonly sessionService: SessionService,
    private readonly advStore: AdvocacyNgReduxFacade,
  ) {}

  ngOnInit(): void {
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);
    // WS2 strangle: advocacy state now lives in the native NgRx feature, reached
    // through the $ngRedux-shaped facade (was $injector.get('$ngRedux')).
    this.$ngRedux = this.advStore;
    this.dateSerializer = dateSerialize;
    this.layerApiMap = {
      'Pre-Stay': AdvocacyImpactApi.getLayerPrestayStories,
      'Homepage': AdvocacyImpactApi.getLayerHomepageStories,
      'Photo Contest': AdvocacyImpactApi.getLayerPhotocontestStories,
      'Post-Stay': AdvocacyImpactApi.getLayerPoststayStories,
      'Quote List': AdvocacyImpactApi.getLayerQuoteListStories,
    };

    this.unsubscribe = this.$ngRedux.subscribe(() => this.syncFromState());
    this.syncFromState();
    this.fetchOverview();
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
  }

  /** Read the relevant slices from the ng-redux store. */
  private syncFromState(): void {
    const state = this.$ngRedux.getState();
    const view = state?.views?.bigPicture ?? {};
    this.layerInfoRequestStatus = view.layerInfoRequestStatus ?? [];
    this.propertyAdvocacyOverview = state?.apiData?.propertyAdvocacyOverview ?? {};
    this.propertyAdvocacyImpact = state?.apiData?.propertyAdvocacyImpact ?? {};
  }

  fetchOverview(startDate?: string, endDate?: string): void {
    this.$ngRedux.dispatch(BigPictureActions.initialized(false));
    this.startDate = new Date(this.snapshot.get().start);
    this.endDate = new Date(this.snapshot.get().end);
    const s = startDate ?? this.dateSerializer(this.startDate);
    const e = endDate ?? this.dateSerializer(this.endDate);
    this.$ngRedux
      .dispatch(BigPictureActions.fetchAdvocacyOverview(s, e))
      .then(() => {
        this.startDate = new Date(s);
        this.endDate = new Date(e);
        this.isInitialized = true;
        this.activateFirstSection();
        this.fetchFirstSectionData();
        this.$ngRedux.dispatch(BigPictureActions.initialized(true));
      });
  }

  /** Template helper — whether the requested layer's data has come back yet. */
  isLayerLoaded(layerName: string): boolean {
    return !!this.layerInfoRequestStatus.find((l) => l.name === layerName)?.isLoaded;
  }

  isLayerActive(layerName: string): boolean {
    return !!(
      this.propertyAdvocacyOverview?.activeLayers &&
      this.propertyAdvocacyOverview.activeLayers.some((l: any) => l.name === layerName)
    );
  }

  hasAnyLayersActive(): boolean {
    return !!(
      this.propertyAdvocacyOverview?.activeLayers &&
      this.propertyAdvocacyOverview.activeLayers.length > 0
    );
  }

  fetchLayerData(layerName: string): void {
    const status = this.layerInfoRequestStatus.find((l) => l.name === layerName);
    if (!status || status.isLoaded) return;
    const start = this.dateSerializer(new Date(this.startDate ?? this.snapshot.get().start));
    const end = this.dateSerializer(new Date(this.endDate ?? this.snapshot.get().end));
    this.$ngRedux.dispatch(this.layerApiMap[layerName](this.property.UUID, start, end));
  }

  onCalendarDateRangeChange(dateRange: DateRange): void {
    this.fetchOverview(this.dateSerializer(dateRange.fromDate), this.dateSerializer(dateRange.toDate));
  }

  /** Public for template binding — clicking a tab. */
  activateSection(section: SectionEntry): void {
    for (const s of this.sectionsRegistry) s.isActive = false;
    section.isActive = true;
    this.fetchLayerData(section.layerName);
  }

  /** Template helper — read the snapshot defaults when startDate/endDate aren't loaded yet. */
  get snapshotStart(): Date | undefined {
    return this.startDate ?? (this.snapshot ? new Date(this.snapshot.get().start) : undefined);
  }
  get snapshotEnd(): Date | undefined {
    return this.endDate ?? (this.snapshot ? new Date(this.snapshot.get().end) : undefined);
  }

  private activateFirstSection(): void {
    if (!this.propertyAdvocacyOverview?.activeLayers?.length) return;
    for (const layer of LAYERS_ORDER) {
      if (this.propertyAdvocacyOverview.activeLayers.find((al: any) => al.name === layer)) {
        for (const s of this.sectionsRegistry) s.isActive = s.layerName === layer;
        break;
      }
    }
  }

  private fetchFirstSectionData(): void {
    if (!this.propertyAdvocacyOverview?.activeLayers?.length) return;
    for (const layer of LAYERS_ORDER) {
      if (this.propertyAdvocacyOverview.activeLayers.find((al: any) => al.name === layer)) {
        this.fetchLayerData(layer);
        break;
      }
    }
  }
}
